import React, { useMemo } from "react";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";

const PriceSensitivityPanel = ({ data, loading }) => {
  const { chartData, dropCallout } = useMemo(() => {
    if (!data?.length) return { chartData: [], dropCallout: null };

    const cd = data.map((b) => ({
      ...b,
      // 2026-08-13 — the RPC returns `conv_rate`, not `conversion_pct`. Map it
      // so the Line's dataKey="conversion_pct" resolves. Without this the line
      // curve was invisible (all undefined values).
      conversion_pct: b.conversion_pct ?? b.conv_rate ?? 0,
      name: b.price_bucket,
    }));

    let worstDrop = 0;
    let worstBucket = null;
    for (let i = 1; i < cd.length; i++) {
      const drop = cd[i - 1].conversion_pct - cd[i].conversion_pct;
      if (drop > worstDrop) {
        worstDrop = drop;
        worstBucket = cd[i].price_bucket;
      }
    }

    return { chartData: cd, dropCallout: worstBucket };
  }, [data]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-10 bg-gray-200 dark:bg-gray-800 rounded w-2/3" />
        <div className="h-48 bg-gray-200 dark:bg-gray-800 rounded" />
      </div>
    );
  }

  const totalSessions = (data || []).reduce((sum, b) => sum + (b.sessions || 0), 0);

  if (!data?.length || totalSessions < 50) {
    return (
      <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-sm">
        Not enough sessions with rate data to analyze price sensitivity.
      </div>
    );
  }

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    return (
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg text-sm">
        <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">{d.price_bucket}/night</div>
        <div className="text-gray-600 dark:text-gray-400">{d.sessions} sessions · {d.bookings} booked</div>
        <div className="text-gray-600 dark:text-gray-400">{d.conversion_pct}% conversion rate</div>
      </div>
    );
  };

  return (
    <div>
      {/* KPI callout */}
      {dropCallout && (
        <div className="mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Conversion drops at <span className="font-semibold text-red-600 dark:text-red-400">{dropCallout}</span> range
          </div>
        </div>
      )}

      {/* Chart */}
      <ResponsiveContainer width="100%" height={220}>
        <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(156,163,175,0.2)" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 10, fill: "#9CA3AF" }}
            axisLine={{ stroke: "rgba(156,163,175,0.3)" }}
            tickLine={false}
            interval={0}
            angle={-25}
            textAnchor="end"
            height={40}
          />
          <YAxis
            yAxisId="left"
            tick={{ fontSize: 11, fill: "#9CA3AF" }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fontSize: 11, fill: "#9CA3AF" }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `${v}%`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar
            yAxisId="left"
            dataKey="sessions"
            radius={[4, 4, 0, 0]}
          >
            {chartData.map((entry) => (
              <Cell
                key={entry.price_bucket}
                fill={entry.price_bucket === dropCallout ? "#EF4444" : "rgba(50,177,255,0.3)"}
              />
            ))}
          </Bar>
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="conversion_pct"
            stroke="#10b981"
            strokeWidth={2}
            dot={{ fill: "#10b981", r: 3 }}
          />
        </ComposedChart>
      </ResponsiveContainer>

      {/* Legend — stacks vertically (each label full-width) below 500px so the captions don't wrap awkwardly */}
      <div className="flex flex-col min-[500px]:flex-row min-[500px]:items-center gap-2 min-[500px]:gap-4 mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-1.5 w-full min-[500px]:w-auto">
          <div className="w-3 h-2 rounded-sm bg-[#32B1FF]/30 shrink-0" />
          <span className="text-sm text-gray-500 dark:text-gray-400">Sessions</span>
        </div>
        <div className="flex items-center gap-1.5 w-full min-[500px]:w-auto">
          <div className="w-3 h-2 rounded-sm bg-red-500/70 shrink-0" />
          <span className="text-sm text-gray-500 dark:text-gray-400">Biggest drop-off</span>
        </div>
        <div className="flex items-center gap-1.5 w-full min-[500px]:w-auto">
          <div className="w-3 h-1 rounded-full bg-emerald-500 shrink-0" />
          <span className="text-sm text-gray-500 dark:text-gray-400">Conversion %</span>
        </div>
      </div>
    </div>
  );
};

export default PriceSensitivityPanel;
